/*
 * Figma `95:2` — Toggle, and the Ticket Delivery channel rows at `117:260`.
 *
 * A checkbox underneath, not a button with `role="switch"`. The frozen design uses this control for
 * settings that are part of a form and saved with it — delivery channels, Coming Soon's hero image,
 * a payment account's active flag — and a checkbox is what a form submits, what a label click
 * toggles, and what the space bar already operates.
 *
 * `consequence` is not decoration. Every toggle in the v3 contract changes something a buyer or an
 * organizer will feel, and the design requires that consequence to be readable *before* the switch
 * is thrown rather than discovered in a toast afterwards.
 */
import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  /** What changes for a buyer or an organizer when this is on. Rendered under the label. */
  consequence?: ReactNode;
  /** Shown at the end of the row — a readiness badge, a channel's last-verified time. */
  trailing?: ReactNode;
}

export function Toggle({ label, consequence, trailing, className = '', checked, disabled, ...rest }: ToggleProps) {
  const id = useId();
  const consequenceId = `${id}-consequence`;

  return (
    <div className={`jt-toggle ${className}`.trim()} data-disabled={disabled || undefined}>
      <input
        {...rest}
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        className="jt-toggle-input"
        aria-describedby={consequence ? consequenceId : undefined}
      />
      <label className="jt-toggle-body" htmlFor={id}>
        <span className="jt-toggle-track" aria-hidden="true"><span className="jt-toggle-thumb" /></span>
        <span className="jt-toggle-copy">
          <span className="jt-toggle-label">{label}</span>
          {consequence && <span className="jt-toggle-consequence" id={consequenceId}>{consequence}</span>}
        </span>
      </label>
      {trailing && <span className="jt-toggle-trailing">{trailing}</span>}
    </div>
  );
}
